import React, { FC } from "react"
import classNames from "classnames"
import { SbBlokData, SbRichTextDoc, renderRichText } from "@storyblok/react"
import Image from "next/image"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, Pagination } from "swiper/modules"
import "swiper/css"
import "swiper/css/pagination"

import { BlokImage } from ".."
import StarRating from "../../components/star-rating-v2"
import Verifications from "../../components/verifications"
import star from "../../assets/vector/star-brown.svg"
import { readImage } from "../../app/utils/read-image"
import { safelyRenderHtml } from "../../util/safelyRenderHtml"

type Image = {
  src: string
  alt: string
}

// Storyblok stores presets as <desktop>-<mobile>.
export type ThumbnailSetTextAlignment =
  | "left-center"
  | "center-left"
  | "center-center"
  | "left-left"

const defaultTextAlignmentClasses = "text-left lg:text-center"

const textAlignmentClasses: Record<ThumbnailSetTextAlignment, string> = {
  "left-center":
    "text-center lg:text-left [&_p]:!text-center lg:[&_p]:!text-left",
  "center-left":
    "text-left lg:text-center [&_p]:!text-left lg:[&_p]:!text-center",
  "center-center":
    "text-center lg:text-center [&_p]:!text-center lg:[&_p]:!text-center",
  "left-left": "text-left lg:text-left [&_p]:!text-left lg:[&_p]:!text-left",
}

export interface ThumbnailItem {
  content: string
  companyName: string
  rating: string
  image: BlokImage | Image
  shipmentsComplete: string
  longContent?: boolean
  verified?: boolean
  onCallVet?: boolean
  usdaRegistered?: boolean
  insuranceVerified?: boolean
  review: string
}

export interface ThumbnailSetMobileCopy {
  title?: SbRichTextDoc | string
  subTitle?: SbRichTextDoc | string
}

export interface IItem extends SbBlokData {
  items?: ThumbnailItem[]
  title?: SbRichTextDoc | string
  subTitle?: SbRichTextDoc | string
  mobileCopy?: ThumbnailSetMobileCopy[]
  textAlignment?: string
}
export const Thumbnail: FC<ThumbnailItem> = ({
  image,
  companyName,
  rating,
  shipmentsComplete,
  content,
  verified = true,
  onCallVet = true,
  usdaRegistered = true,
  insuranceVerified = true,
  review,
}) => {
  return (
    <section className="ml-auto w-full  md:min-h-full">
      <div className="grid gap-4 grid-cols-[82px_1fr] items-center">
        <div className="">
          <Image
            className="object-cover rounded w-[82px] h-[82px]"
            src={readImage(image)}
            alt={image?.alt || ""}
            width={82}
            height={82}
          />
        </div>
        <div>
          <div className="text-base font-bold mb-2 font-merriweather text-secondary-900">
            {companyName}
          </div>

          <StarRating
            rating={parseInt(rating)}
            star={{ ...star, width: 16, height: 16 }}
          />
          <p className="mb-2.5 mt-2 text-sm font-light">
            {shipmentsComplete} shipments so far
          </p>
        </div>
      </div>
      <div className="hidden md:block mt-6">
        <Verifications
          verified={verified}
          onCallVet={onCallVet}
          usdaRegistered={usdaRegistered}
          insuranceVerified={insuranceVerified}
          isThumbnail
        />
      </div>

      <p className={`mb-8 mt-6 text-[15px] font-inter`}>”{content}”</p>
      <p className="text-gray14">{review}</p>
    </section>
  )
}

function renderRichTextOrString(value?: SbRichTextDoc | string): string {
  if (!value) {
    return ""
  }

  if (typeof value === "string") {
    return value
  }

  const html = renderRichText(value) || ""

  // Storyblok returns an empty richtext field as a doc with one blank
  // paragraph, which renders as "<p></p>" — treat that as no content.
  return html.replace(/<p>\s*<\/p>/g, "").trim() ? html : ""
}

function isThumbnailSetTextAlignment(
  value?: string,
): value is ThumbnailSetTextAlignment {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(textAlignmentClasses, value)
  )
}

type ThumbnailSetCopyValue = SbRichTextDoc | string

interface ThumbnailSetCopyProps {
  desktopCopy?: ThumbnailSetCopyValue
  mobileCopy?: ThumbnailSetCopyValue
  alignmentClasses: string
  className: string
  // Only the desktop copy is promoted, so a mobile override never adds a
  // second heading. Rich text renders block <p> elements, which can't sit
  // inside a heading, so it stays a div.
  headingTag?: "h2"
}

type ThumbnailSetSubTitleProps = Omit<
  ThumbnailSetCopyProps,
  "className" | "headingTag"
>

interface ThumbnailSetTitleProps extends ThumbnailSetSubTitleProps {
  asHeading: boolean
}

const thumbnailSetTitleClasses =
  "font-merriweather font-normal mb-6 text-3xl leading-[48px] lg:text-[40px] lg:leading-[72px] [&_p]:mb-4 [&_p:last-child]:mb-0"
const thumbnailSetSubTitleClasses =
  "mb-5 -mt-2 md:mt-0 md:mb-8 pb-2 text-lg leading-[40px] md:text-2xl md:leading-[36px] font-inter [&_p]:mb-4 [&_p:last-child]:mb-0"

const ThumbnailSetCopy = ({
  desktopCopy,
  mobileCopy,
  alignmentClasses,
  className,
  headingTag,
}: ThumbnailSetCopyProps) => {
  const renderedDesktopCopy = renderRichTextOrString(desktopCopy)
  const renderedMobileCopy = renderRichTextOrString(mobileCopy)
  const hasMobileCopyOverride = Boolean(renderedMobileCopy.trim())
  const isPlainStringDesktopCopy = typeof desktopCopy === "string"
  const isPlainStringMobileCopy = typeof mobileCopy === "string"

  const DesktopCopyTag = (isPlainStringDesktopCopy && headingTag) || "div"

  const renderCopy = (copy: string, isPlainString: boolean) =>
    isPlainString ? copy : safelyRenderHtml(copy)

  if (hasMobileCopyOverride) {
    return (
      <>
        <div className={`${className} ${alignmentClasses} lg:hidden`}>
          {renderCopy(renderedMobileCopy, isPlainStringMobileCopy)}
        </div>
        {renderedDesktopCopy && (
          <DesktopCopyTag
            className={`hidden lg:block ${className} ${alignmentClasses}`}
          >
            {renderCopy(renderedDesktopCopy, isPlainStringDesktopCopy)}
          </DesktopCopyTag>
        )}
      </>
    )
  }

  if (!renderedDesktopCopy) {
    return null
  }

  return (
    <DesktopCopyTag className={`${className} ${alignmentClasses}`}>
      {renderCopy(renderedDesktopCopy, isPlainStringDesktopCopy)}
    </DesktopCopyTag>
  )
}

const ThumbnailSetTitle: FC<ThumbnailSetTitleProps> = ({
  desktopCopy,
  mobileCopy,
  alignmentClasses,
  asHeading,
}) => (
  <ThumbnailSetCopy
    desktopCopy={desktopCopy}
    mobileCopy={mobileCopy}
    alignmentClasses={alignmentClasses}
    className={thumbnailSetTitleClasses}
    headingTag={asHeading ? "h2" : undefined}
  />
)

const ThumbnailSetSubTitle: FC<ThumbnailSetSubTitleProps> = ({
  desktopCopy,
  mobileCopy,
  alignmentClasses,
}) => (
  <ThumbnailSetCopy
    desktopCopy={desktopCopy}
    mobileCopy={mobileCopy}
    alignmentClasses={alignmentClasses}
    className={thumbnailSetSubTitleClasses}
  />
)

// Desktop (Tailwind lg) carousel layout. The SwiperSlide class
// `lg:!w-[calc((100%-64px)/3)] lg:mr-[32px]` hard-codes these values so the
// server render matches Swiper's hydrated layout: change both together.
const DESKTOP_BREAKPOINT_PX = 1024
const DESKTOP_SLIDES_PER_VIEW = 3
const DESKTOP_SPACE_BETWEEN_PX = 32

interface ThumbnailSetProps {
  blok: IItem
  // Opt out when the same set is mounted twice for responsive layouts, so the
  // page keeps a single section heading.
  titleAsHeading?: boolean
}

const ThumbnailSet = ({ blok, titleAsHeading = true }: ThumbnailSetProps) => {
  const { items, title, subTitle, mobileCopy, textAlignment } = blok
  const mobileCopyOverride = mobileCopy?.[0]
  const alignmentClasses = isThumbnailSetTextAlignment(textAlignment)
    ? textAlignmentClasses[textAlignment]
    : defaultTextAlignmentClasses
  // With this many cards Swiper locks at lg (one snap point) and hides the
  // dots, so cards can drop the margin that keeps the dots clear of them.
  const fitsOneDesktopRow = (items?.length ?? 0) <= DESKTOP_SLIDES_PER_VIEW

  return (
    <section
      id="thumbnail-set"
      className="max-w-[1334px] mx-auto px-4 mt-1 mb-8 md:px-10 md:mt-2 md:mb-10 lg:mt-0 lg:mb-8"
    >
      <ThumbnailSetTitle
        desktopCopy={title}
        mobileCopy={mobileCopyOverride?.title}
        alignmentClasses={alignmentClasses}
        asHeading={titleAsHeading}
      />
      <ThumbnailSetSubTitle
        desktopCopy={subTitle}
        mobileCopy={mobileCopyOverride?.subTitle}
        alignmentClasses={alignmentClasses}
      />
      <Swiper
        spaceBetween={30}
        centeredSlides={true}
        autoplay={{
          delay: 2500,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
        }}
        // With up to three cards Swiper locks at lg (watchOverflow) and hides
        // the dots, so the carousel lays out like a three-column grid.
        breakpoints={{
          [DESKTOP_BREAKPOINT_PX]: {
            slidesPerView: DESKTOP_SLIDES_PER_VIEW,
            spaceBetween: DESKTOP_SPACE_BETWEEN_PX,
            centeredSlides: false,
          },
        }}
        modules={[Autoplay, Pagination]}
        className="mySwiper"
      >
        {items?.map((item, index) => (
          // The lg width and margin match what Swiper sets after hydration
          // (see the DESKTOP_* constants), so the server render is already
          // three columns. lg:!h-auto lets slides stretch to the tallest card.
          <SwiperSlide
            key={index}
            className="lg:!h-auto lg:!w-[calc((100%-64px)/3)] lg:mr-[32px]"
          >
            <div
              className={classNames(
                "border bg-secondary-100 border-secondary-300 p-6 rounded-3xl mb-10",
                fitsOneDesktopRow && "lg:mb-0 lg:h-full",
              )}
            >
              <Thumbnail
                companyName={item.companyName}
                shipmentsComplete={item.shipmentsComplete}
                rating={item.rating}
                verified={item.verified}
                onCallVet={item.onCallVet}
                usdaRegistered={item.usdaRegistered}
                insuranceVerified={item.insuranceVerified}
                content={item.content}
                review={item.review}
                image={item.image}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}

export default ThumbnailSet
